import { IDocumentMetadata } from "contracts/documents";
import { ISignatureProviderSettings } from "../../Global/Base/Crypto/ISignatureProvider";
import { IDisposable } from "../../Global/Base/IDisposable";
import { instanceOf } from "../../Global/Base/Polyfill/instanceOf";
import { HashType } from "../../Global/Base/Security/HashType";
import { RequestContext } from "../../OW.Remoting/Remoting/RequestContext";
import { CommandType } from "./Messages/CommandType";
import { IMessageBody, IResultMessage } from "./Messages/MessagesInterface";
import { TransferEncoding } from "./Messages/TransferEncoding";
import { SignatureStreamSettings } from "./SignatureStreamSettings";

/**
 * @includeToDoc
 */
export interface IWritableStream extends IStream {
    /**
     * Název reprezentující data
     */
    name: string;

    /**
     * Zapíše do streamu (a pošle do doplňku) kus dat
     * @param data zapisovaná data v textové nebo binární podobě
     * @param isLastBlock zda je zapisovaný blok poslední
     */
    writeBlock(data: string | ArrayBuffer, isLastBlock: boolean): Promise<void>;
}

/**
 * @includeToDoc
 */
export interface ISigningAppCommunicator extends IDisposable {

    /**
     * Unikátní identifikátor právě otevřené session
     */
    sessionId: string | undefined;

    /**
     * Vytvoří dlouhodobé spojení pro komunikaci s extension. Všechny následující příkazy budou patřit do této session.
     * Session je nutné uzavřít pomocí volání closeSession().
     * @param lang Nepovinný atribut určující jazyk GUI, který je potřebný pouze v případě, že se v rámci session bude otevírat GUI, jinak by se neměl posílat. Jazyk musí být ISO 639-1 (en, cs, ...).
     */
    openSession(lang?: string): Promise<void>;

    /**
     * Uzavře existující spojení s extension. Je nutné volat vždy v páru s openSession().
     * Pokud žádné otevřené spojení neexistuje, tak dojde k tichému skončení.
     */
    closeSession(): Promise<void>;

    /**
     * Odešle zprávu do extension
     * @param message odesílaná data
     */
    postMessage(command: CommandType, body?: IMessageBody): Promise<IResultMessage>;
}

export class ISigningAppCommunicator {
    private static _properties = ["sessionId", "openSession", "closeSession", "postMessage"];

    // v kódu který se kompiluje pro ES5, je nutné aby funkce [Symbol.hasInstance] byla definovaná jako property, viz ISignatureManager
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && !ISigningAppCommunicator._properties.some((value) => !Reflect.has(instance, value)) &&
            instanceOf(instance, IDisposable);
    }
}

/**
 * Rozhraní streamu poskytujícího data pro vytváření podpisů
 * @includeToDoc
 */
export interface ISignatureDataStream {

    /**
     * Načítá celý obsah streamu
     * @param chunkSize Velikost, po které se mají data načítat
     * @param onChunkDownloaded Callback funkce, která je volaná při načtení části dat
     */
    read(chunkSize: number, onChunkDownloaded: (chunk: ArrayBuffer, isLastChunk: boolean) => Promise<void>): Promise<void>;

    /**
     * Načte název dat
     * @returns Název dat
     */
    getName(): Promise<string>;

    /**
     * Načítá veškerá metadata ve formátu klíč - hodnota
     * @returns Metadata
     */
    getMetadata(): Promise<IDocumentMetadata | null>;

    /**
     * Vygeneruje URI pro referenci ukazující na data v podpisu
     * @returns URI
     */
    getReferenceUri(): Promise<string>;
}

/**
 * Skupina streamů k podepsání
 * @includeToDoc
 */
export interface ISignGroup {
    providerId: string;
    provider: ISignatureProviderSettings;
    inputStreams: ISigningStream[];
}

/**
 * Rozhraní pro jeden stream k podepsání
 * @includeToDoc
 */
export interface ISigningStream {
    /**Stream pro podepsání*/
    signingStream: ISignatureDataStream;
    /**Stream pro zobrazení*/
    displayStream: ISignatureDataStream;
    /**Předchozí podpis*/
    currentSignatureStream?: ISignatureDataStream;
}

/**
 * Skupina streamů odesílaných do nativní aplikace
 * @includeToDoc
 */
export interface ISignGroupWritables {
    /**Podpisový provider*/
    provider: ISignatureProviderSettings;
    //provider: IProviderInfo;
    /**Streamy odesílané do nativní aplikace*/
    streams: ISignGroupStream[];
}

/**
 * Rozhraní pro jeden stream odesílaný do nativní aplikace
 * @includeToDoc
 */
export interface ISignGroupStream {
    /**Stream pro podepsání*/
    signingStream: IWritableStream;
    /**Stream pro zobrazení*/
    displayStream: IWritableStream;
    /**Předchozí podpis*/
    currentSignatureStream?: IWritableStream;
}

/**
 * Skupina podepsaných streamů
 * @includeToDoc
 */
export interface ISignedGroupInfo {
    /**ID skupiny*/
    groupId: string;
    /**Kolekce informací o podepsaných streamech*/
    infos: ISignedInfo[];
}

/**
 * @includeToDoc
 */
export interface ISigningSession extends IDisposable {
    /**
     * Otevře spojení, bez tohoto volání není session platná
     * @param lang Nepovinný atribut určující jazyk GUI, který je potřebný pouze v případě, že se v rámci session bude otevírat GUI, jinak by se neměl posílat. Jazyk musí být ISO 639-1 (en, cs, ...).
     */
    open(lang?: string): Promise<void>;

    /**
     * Vytvoří podpis textového řetězce
     * @param signingGroups kolekce podepisovaných streamů.
     */
    createTextSignatures(...signingGroups: ISignGroup[]): Promise<ISignedGroupInfo[]>;

    /**
     * Vytvoří podpis dokumentů
     * @param providerSettings konfigurace podpisového provideru, které určují, jaký podpis má vytvářet
     * @param inputStreams libovolný počet streamů s daty a metadaty určenými pro podpis
     */
    createDocumentSignatures(providerSettings: ISignatureProviderSettings, ...inputStreams: ISignatureDataStream[]): Promise<ISignedInfo[]>;

    /**
     * Hromadný podpis skupin dokumentů.
     * @param signingGroups Pole skupin dokumentů.
     */
    signDocumentGroups(...signingGroups: ISignGroup[]): Promise<ISignedGroupInfo[]>;

    /**
     * Vytvoří kontrapodpis dokumentu
     * @param providerSettings konfigurace podpisového provideru, které určují, jaký podpis má vytvářet
     * @param signatureStream stream ve kterém je stávající podpis
     * @param detachedStream stream s daty v případě detached podpisu
     */
    createCounterSignature(providerSettings: ISignatureProviderSettings,
        signatureStream: ISignatureDataStream,
        detachedStream?: ISignatureDataStream): Promise<ISignedInfo[]>;

    /**
     * Dešifruje asynchronní klíč
     * @param data nastaveni provideru a seznam klicu
     */
    decryptObjects(data: ICryptGroup): Promise<any>;
}

export class ISigningSession {
    private static _properties = ["open", "createTextSignatures", "createDocumentSignatures", "decryptObjects", "signDocumentGroups"];

    // v kódu který se kompiluje pro ES5, je nutné aby funkce [Symbol.hasInstance] byla definovaná jako property, viz ISignatureManager
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && !ISigningSession._properties.some((value) => !Reflect.has(instance, value)) &&
            instanceOf(instance, IDisposable);
    }
}

/**
 * Provider pro šifrování a dešifrování
 * @includeToDoc
 */
export interface ICryptoProvider {

    /**
    * Název algoritmu dle specifikace JWK.
    * http://www.w3.org/TR/WebCryptoAPI/#jwk-mapping-alg
    * https://tools.ietf.org/html/rfc7518#appendix-A.2
    */
    //algorithmName: string;

    type: string;
}

/**
     * @includeToDoc
     */
export interface IReadableStream extends IStream {
    /**
     * Název reprezentující data
     */
    name: string;

    /**
     * Velikost streamu
     */
    size: number;

    /**
     * Aktuální pozice ve streamu.
     */
    position: number;

    /**
     * Převine stream na začátek
     */
    begin(): Promise<void>;

    /**
     * Značí, zda čtení došlo do konce streamu
     */
    isEnd(): boolean;

    /**
    * Načte datový blok a podle zadaného kódování jej vrátí jako PlainText nebo Base64
    * @param length Požadovaná velikost datového bloku.
    */
    readBlock(length: number, encoding: TransferEncoding): Promise<string>;

    /**
    * Vrátí hash obsahu celého streamu
    */
    getHash(hashType: HashType): Promise<string>;
}

/**
   * @includeToDoc
   */
export interface IStream {
    streamId: string;
}

/**
     * @includeToDoc
     */
export interface ISignedInfo {
    error?: Error;

    stream?: IReadableStream;

    containsDocument: boolean;
}

/**
* @includeToDoc
*/
export interface ISignatureManager {
    openSession(): Promise<ISigningSession>;
}

export class ISignatureManager {
    private static _properties = ["openSession"];

    // v kódu který se kompiluje pro ES5
    // je nutné aby funkce  >> public static [Symbol.hasInstance](instance: any): boolean << byla definovaná jako property >> public static get[Symbol.hasInstance](): (instance: any) => boolean <<,
    // aby výsledný kód byl definován přes Object.defineProperty, pokud se totiž v ES5 pokoušíme udělat ClassName[Symbol.hasInstance] = function(instance){...},
    // tak nám v tom brání Object.getOwnPropertyDescriptor(Function.prototype, Symbol.hasInstance).writable === false
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && !ISignatureManager._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Rozhraní pro úpravu procesu podepisování na FileEditu
 * @includeToDoc
 */
export interface ISigningStreamCreator {
    /**
     * Metoda vytváří stream(y), které se budou podepisovat.
     */
    createSignatureStreams(settings: SignatureStreamSettings[]): Promise<ISignatureDataStream[]>;
    /**
     * Metoda vytváří stream(y), které se budou podepisovat kontra podpisem.
     * @param settings
     * @param requestContext Viz {@link RequestContext}
     */
    createCounterSignatureStreams(settings: SignatureStreamSettings[], requestContext?: RequestContext): Promise<ICounterSignatureStreams[]>;
}

export class ISigningStreamCreator {
    private static _properties = ["createSignatureStreams", "createCounterSignatureStreams"];

    // v kódu který se kompiluje pro ES5, je nutné aby funkce [Symbol.hasInstance] byla definovaná jako property, viz ISignatureManager
    public static get [Symbol.hasInstance](): (instance: any) => boolean {
        return (instance: any) => instance != null && !ISigningStreamCreator._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * @includeToDoc
 */
export interface ICryptGroup {
    provider: ICryptoProvider;
    keys: Array<{ key: string; keyId: string }>;
    certificate: string;
}

/**
 * @includeToDoc
 */
export interface ICryptedGroup {
    keys: Array<{ error?: any; key: string; keyId: string }>;
}

/**
 * Rozhraní streamu poskytujícího data pro vytváření kontra podpisu.
 * @includeToDoc
 */
export interface ICounterSignatureStreams {

    /**
     * Stream ve kterém je stávající podpis
     */
    signatureStream: ISignatureDataStream;

    /**
     * Stream s daty v případě detached podpisu
     */
    documentStream?: ISignatureDataStream;

}
